import React, { useState, useEffect, useRef } from "react";
import IntervalPreQuiz from "./IntervalPreQuiz";
import IntervalQuiz, { IntervalQuizOptions } from "./IntervalQuiz";
import PlaybackOptionsPanel from "../../../components/PlaybackOptionsPanel";
import PianoKeyboard from "../../../components/PianoKeyboard";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { useSoundLibrary } from "../../../hooks/useSoundLibrary";
import { useDrillLocalStorage } from "../../../hooks/useDrillLocalStorage";
import { usePlaybackSettings } from "../../../hooks/usePlaybackSettings";
import { Note } from "../../../soundLibrary";
import { ROUTES } from "../../../config/routes";
import { SEO_METADATA } from "../../../config/seoMetadata";
import SEO from "../../../components/SEO";
import styles from "../../../styles/IntervalQuiz.module.css";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const noteString = (noteIndex: number) => {
    const octave = Math.floor(noteIndex / 12);
    const name = NOTES[noteIndex % 12];
    return `${name}${octave}`;
};

const getRandomNoteIndex = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const defaultIntervalOptions: IntervalQuizOptions = {
    availableIntervals: [
        { name: "Minor 3rd", semitones: 3 },
        { name: "Major 3rd", semitones: 4 },
    ],
    mode: "questions",
    questionLimit: 10,
    timeLimitSeconds: 60,
};

export default function IntervalQuizWrapper() {
    const { playNote, playChord, stopAll } = useSoundLibrary();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isMountedRef = useRef(true);

    // Persist quiz options to localStorage
    const [quizOptions, setQuizOptions] = useDrillLocalStorage<IntervalQuizOptions>(
        "drill_interval_storage",
        defaultIntervalOptions
    );

    // Persist playback settings to localStorage (shared across all drills)
    const [playbackOptions, setPlaybackOptions] = usePlaybackSettings();

    const [quizStarted, setQuizStarted] = useState<boolean>(false);
    const [showPiano, setShowPiano] = useState(false);

    const [questionCount, setQuestionCount] = useState(0);
    const [attemptedQuestions, setAttemptedQuestions] = useState(0);
    const [displayedQuestionIndex, setDisplayedQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(quizOptions.timeLimitSeconds || 0);
    const [feedback, setFeedback] = useState<{ message: string; color: string } | null>(null);
    const [quizEnded, setQuizEnded] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState<{
        note1: Note;
        note2: Note;
        correctAnswer: string;
        questionOptions: string[];
    } | null>(null);

    const handlePlayInterval = async () => {
        if (!currentQuestion) return;
        
        let activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
        if (playbackOptions.playMode === "melodic") {
            playNote(playbackOptions.instrument, currentQuestion.note1, {
                velocity: activeVolume,
                duration: 0.7,  // 0.7 second note duration
            });
            await new Promise((res) => setTimeout(res, 700));  // Wait for note to finish
            stopAll();  // Cut off the note
            await new Promise((res) => setTimeout(res, 200));  // Gap between notes
            
            playNote(playbackOptions.instrument, currentQuestion.note2, {
                velocity: activeVolume,
                duration: 0.7,  // 0.7 second note duration
            });
            await new Promise((res) => setTimeout(res, 700));  // Wait for note to finish
        } else {
            await playChord(playbackOptions.instrument, [currentQuestion.note1, currentQuestion.note2], {
                velocity: activeVolume,
            });
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            stopAll();
        };
    }, [stopAll]);

    const handleStart = () => {
        setQuizStarted(true);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setScore(0);
        setTimeLeft(quizOptions.timeLimitSeconds || 0);
        setFeedback(null);
        setCurrentQuestion(null);
    };

    const disableStart = () => {
        if (!quizOptions || !quizOptions.availableIntervals) return true;
        return quizOptions.availableIntervals.length < 2;
    };

    const generateQuestion = () => {
        if (!quizStarted || !quizOptions.availableIntervals.length || quizEnded) return;

        const rootNoteIndex = getRandomNoteIndex(48, 71);
        const interval = quizOptions.availableIntervals[Math.floor(Math.random() * quizOptions.availableIntervals.length)];
        const secondNoteIndex = rootNoteIndex + interval.semitones;
        const note1Str = noteString(rootNoteIndex) as Note;
        const note2Str = noteString(secondNoteIndex) as Note;

        const allIntervalNames = quizOptions.availableIntervals.map((i) => i.name);

        const newQuestion = {
            note1: note1Str,
            note2: note2Str,
            correctAnswer: interval.name,
            questionOptions: allIntervalNames,
        };

        setCurrentQuestion(newQuestion);
    };

    const endQuiz = () => {
        // If there's positive feedback, the current question was answered correctly but not yet counted
        if (feedback && feedback.color === "green") {
            setQuestionCount(questionCount + 1);
        }
        setQuizEnded(true);
        setFeedback(null);
        setCurrentQuestion(null);
    };

    useEffect(() => {
        if (quizEnded) {
            // Scroll to top of page when quiz ends
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded]);

    useEffect(() => {
        if (quizStarted) {
            // Scroll to top of page when quiz starts
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizStarted]);

    const resetQuiz = () => {
        setQuizStarted(false);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setDisplayedQuestionIndex(0);
        setScore(0);
        setFeedback(null);
        setQuizEnded(false);
        setCurrentQuestion(null);
        // Note: quizOptions and playbackOptions are preserved from localStorage

        // Scroll to top when resetting quiz
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        if (quizOptions.mode === "time" && timeLeft > 0 && quizStarted) {
            const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [quizOptions.mode, timeLeft, quizStarted]);

    useEffect(() => {
        if (quizStarted && !currentQuestion && !quizEnded) {
            generateQuestion();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizStarted, currentQuestion, quizEnded]);

    useEffect(() => {
        if (currentQuestion && quizStarted && !quizEnded) {
            setDisplayedQuestionIndex(displayedQuestionIndex + 1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion, quizStarted, quizEnded]);

    useEffect(() => {
        if (currentQuestion && quizStarted && !quizEnded) {
            const timer = setTimeout(() => {
                handlePlayInterval();
            }, 300);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion, quizStarted, quizEnded]);

    return (
        <>
            <SEO {...SEO_METADATA.DRILL_INTERVALS} />
            <div ref={wrapperRef} className={styles.intervalsDrillWrapper}>
            <div className={styles.intervalsDrillHeader}>
                <div className={styles.titleSection}>Intervals Drill</div>

                {!quizStarted && (
                    <div className={styles.headerTop}>
                        <button
                            onClick={handleStart}
                            disabled={disableStart()}
                            className={styles.startQuizButton}
                        >
                            Start Quiz
                        </button>
                    </div>
                )}

                {quizStarted && !quizEnded && (
                    <div className={styles.quizControlsPanel}>
                        {(quizOptions.mode !== "endless") && (
                            <div className={styles.quizStatus}>
                                {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                                {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                                 displayedQuestionIndex <= quizOptions.questionLimit && (
                                    <div className={styles.statusItem}>📝 {displayedQuestionIndex + 1}/{quizOptions.questionLimit}</div>
                                )}
                            </div>
                        )}
                        <div className={styles.quizActions}>
                            {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                              (quizOptions.mode === "time" && timeLeft > 0) ||
                              (quizOptions.mode === "endless" && !quizEnded)) && (
                                <button className={styles.playButton} onClick={() => handlePlayInterval()}>
                                    Play
                                </button>
                            )}
                            <button onClick={endQuiz} className={styles.endQuizButton}>
                                End Quiz
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.quizAndPlaybackOpts}>
                {quizStarted ? (
                    <IntervalQuiz
                        {...quizOptions}
                        playbackOptions={playbackOptions}
                        currentQuestion={currentQuestion}
                        setCurrentQuestion={setCurrentQuestion}
                        questionCount={questionCount}
                        setQuestionCount={setQuestionCount}
                        attemptedQuestions={attemptedQuestions}
                        setAttemptedQuestions={setAttemptedQuestions}
                        score={score}
                        setScore={setScore}
                        timeLeft={timeLeft}
                        setTimeLeft={setTimeLeft}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        generateQuestion={generateQuestion}
                        quizEnded={quizEnded}
                        setQuizEnded={setQuizEnded}
                        resetQuiz={resetQuiz}
                    />
                ) : (
                    <div className={styles.preQuizWithBanner}>
                        <LearnMoreBanner
                            to={ROUTES.LEARN.INTERVALS}
                            variant="intro"
                            introTitle="What are intervals?"
                            introText={
                                <>
                                    An <strong>interval</strong> is the distance between two notes.
                                    Training your ear to recognize them lets you play melodies by ear,
                                    transcribe music, and improvise confidently.
                                </>
                            }
                            examplesLabel="Song references to help you remember:"
                            examples={[
                                { primary: "Jaws theme", secondary: "Minor 2nd" },
                                { primary: "Happy Birthday", secondary: "Major 2nd" },
                                { primary: "Star Wars", secondary: "Perfect 5th" },
                            ]}
                        />
                        <IntervalPreQuiz setQuizOptions={setQuizOptions} initialOptions={quizOptions} />
                    </div>
                )}

                <PlaybackOptionsPanel
                    initialOptions={playbackOptions}
                    onChange={setPlaybackOptions}
                />
            </div>

            {!quizStarted && (
                <div className={styles.startQuizFooter}>
                    <button
                        onClick={handleStart}
                        disabled={disableStart()}
                        className={styles.startQuizButton}
                    >
                        Start Quiz
                    </button>
                </div>
            )}

            {quizStarted && !quizEnded && (
                <div className={styles.quizControlsFooter}>
                    {(quizOptions.mode !== "endless") && (
                        <div className={styles.quizStatus}>
                            {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                            {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                             displayedQuestionIndex <= quizOptions.questionLimit && (
                                <div className={styles.statusItem}>📝 {displayedQuestionIndex + 1}/{quizOptions.questionLimit}</div>
                            )}
                        </div>
                    )}
                    <div className={styles.quizActions}>
                        {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                          (quizOptions.mode === "time" && timeLeft > 0) ||
                          (quizOptions.mode === "endless" && !quizEnded)) && (
                            <button className={styles.playButton} onClick={() => handlePlayInterval()}>
                                Play
                            </button>
                        )}
                        <button onClick={endQuiz} className={styles.endQuizButton}>
                            End Quiz
                        </button>
                    </div>
                </div>
            )}

            {/* Piano Keyboard - Standalone */}
            <button
                className={styles.pianoToggleButton}
                onClick={() => setShowPiano(!showPiano)}
            >
                {showPiano ? "Hide Piano ▲" : "Show Piano ▼"}
            </button>
            
            <div
                className={`${styles.pianoCollapsibleWrapper} ${!showPiano ? styles.hidden : ""}`}
            >
                <PianoKeyboard
                    instrument={playbackOptions.instrument}
                    volume={playbackOptions.volume}
                    muted={playbackOptions.muted}
                />
            </div>
        </div>
        </>
    );
}
